/**
 * Loading skeleton for individual sections
 */
import * as React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../../../Theme/Theme';

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const Section = styled.section``;

const SkeletonBox = styled.div<{ width?: string; height?: string; radius?: string }>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '20px'};
  border-radius: ${(props) => props.radius || '8px'};
  background: linear-gradient(
    90deg,
    ${Theme.Colors.lightestGrey} 0%,
    ${Theme.Colors.lighterGrey} 50%,
    ${Theme.Colors.lightestGrey} 100%
  );
  background-size: 2000px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;

  @media (min-width: 768px) {
    gap: 16px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

export const SectionSkeleton: React.FC<{ variant?: 'grid' | 'cards' | 'hero' }> = ({ variant = 'grid' }) => {
  if (variant === 'hero') {
    return (
      <Section>
        <SectionHeader>
          <SkeletonBox width="200px" height="28px" />
        </SectionHeader>
        <SkeletonBox height="160px" radius="16px" />
      </Section>
    );
  }

  if (variant === 'cards') {
    return (
      <Section>
        <SectionHeader>
          <SkeletonBox width="200px" height="28px" />
          <SkeletonBox width="80px" height="20px" />
        </SectionHeader>
        <div style={{ display: 'flex', gap: '16px', overflowX: 'hidden' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ flex: '0 0 280px', background: 'white', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
              <SkeletonBox height="120px" radius="0" />
              <div style={{ padding: '14px' }}>
                <SkeletonBox height="16px" width="70%" />
                <div style={{ marginTop: '8px' }}>
                  <SkeletonBox height="14px" width="50%" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    );
  }

  return (
    <Section>
      <SectionHeader>
        <SkeletonBox width="200px" height="28px" />
      </SectionHeader>
      <Grid>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <SkeletonBox width="24px" height="24px" radius="12px" />
              <div style={{ flex: 1 }}>
                <SkeletonBox height="16px" width="80%" />
                <div style={{ marginTop: '8px' }}>
                  <SkeletonBox height="6px" width="100%" />
                </div>
              </div>
            </div>
          </Card>
        ))}
      </Grid>
    </Section>
  );
};
